import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  SicherheitsstromStand, SsBatterie, SsKreis, SsQuellanlage, SsVorgabe, SsZeichen, SsZentrale,
} from './elektro/sicherheitsstrom';
import { ANNAHMEN_HINWEIS, ERKENNUNGSWEITE_HINWEIS, PDF_HINWEIS } from './elektro/sicherheitsstrom';
import { statusText, zerlegeBefund } from './elektro/sicherheitstechnik';
import { PageHeader } from '../rahmen/PageHeader';
import { UsvAnsicht } from './ElektroUsv';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Sicherheitsstrom ZSV/CPS (W2-A2, 16.10.2026) — Abschnitt „Sicherheitsbeleuchtung".
 *
 * Die Seite LIEST `GET …/elektro/sicherheitsstrom`: Topologie Haupt-/Unterstation, Batterie,
 * Ladung und Lüftung nach DIN EN 50171 / DIN EN IEC 62485-2, Vergleich mit der Dimensionierung
 * der Fachplanung, Spannungsfall im Batteriebetrieb und die Ersatzprüfung der Erkennungsweite.
 * Gerechnet wird auf dem Server, die Seite schreibt nichts.
 *
 * Ein PDF-Knopf (ELT-ZSV) und die Excel-Mappe. Der PDF-Abruf legt nur bei geändertem Stand eine neue
 * Berichtsfassung an, sonst die abgelegte (F11) — das steht am Knopf und über der Seite.
 *
 * Seit W2-A1 ein zweiter Reiter „USV" (UsvAnsicht): Er lädt `GET …/elektro/usv` erst beim Öffnen.
 */
export function ElektroSicherheitsstrom() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <SicherheitsstromAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  return status === 'fehler' ? 'fehler' : status === 'warnung' ? 'warnung' : 'gut';
}

function Statusmarke({ status, gegenAnnahmen }: { status: string; gegenAnnahmen: boolean }) {
  return <StatusBadge zustand={zustandVon(status)} text={statusText(status, gegenAnnahmen)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

export function SicherheitsstromAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<SicherheitsstromStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [reiter, setReiter] = useState<'zsv' | 'usv'>('zsv');

  useEffect(() => {
    let aktiv = true;
    api.elektroSicherheitsstrom(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroSicherheitsstromNachweisLaden(projektId);
      else await api.elektroSicherheitsstromListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Sicherheitsstrom (ZSV/CPS)"
        beschreibung="Batterie, Wiederaufladung und Lüftung der zentralen Sicherheitsstromversorgung nach DIN EN 50171, Spannungsfall im Batteriebetrieb, Vergleich mit der Dimensionierung der Fachplanung und Ersatzprüfung der Erkennungsweite. Gerechnet beim Abruf, nur lesend."
        aktionen={reiter === 'zsv' ? aktionen : undefined}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      <div className="reiter" role="tablist" style={{ marginBottom: 14 }}>
        {([['zsv', 'ZSV/CPS und Sicherheitsbeleuchtung'], ['usv', 'USV']] as const).map(([k, name]) => (
          <button key={k} type="button" role="tab" aria-selected={reiter === k}
                  className={`reiter-knopf${reiter === k ? ' aktiv' : ''}`} onClick={() => setReiter(k)}>
            {name}
          </button>
        ))}
      </div>
      {reiter === 'usv' && <UsvAnsicht api={api} projektId={projektId} />}
      {reiter === 'zsv' && fehler && <ErrorState titel="Der Sicherheitsstrom konnte nicht geladen werden." technisch={fehler} />}
      {reiter === 'zsv' && !fehler && !stand && <LoadingState />}
      {reiter === 'zsv' && stand && stand.kennzahlen.zentralen === 0 && (
        <EmptyState titel="Keine SV-Zentrale angelegt"
                    erklaerung="SV-Zentralen (CPS/ZSV) entstehen in der Verteilerplanung bzw. über die Zentralen der Melde- und Sicherheitstechnik." />
      )}
      {reiter === 'zsv' && stand && stand.kennzahlen.zentralen > 0 && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: SicherheitsstromStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ANNAHMEN_HINWEIS}</p>}
      <KpiReihe>
        <KpiCard titel="SV-Zentralen" wert={k.zentralen}
                 vergleich={`${k.hauptstationen} Haupt · ${k.unterstationen} Unter`} />
        <KpiCard titel="SV-Endstromkreise" wert={k.kreise} vergleich={`${k.svVerteiler} SV-Verteiler`} />
        <KpiCard titel="Leuchten auf den Kreisen" wert={k.leuchten} />
        <KpiCard titel="Rettungszeichen" wert={k.rettungszeichen} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Fehler" wert={k.fehler} zustand={k.fehler > 0 ? 'fehler' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : 'keine'} />
      </KpiReihe>
      <Zentralen zeilen={stand.zentralen} />
      <Batterien zeilen={stand.batterien} />
      <Fachplanung zeilen={stand.fachplanung} />
      <Kreise zeilen={stand.kreise} spannungEndeV={stand.spannungEndeV} />
      <Erkennungsweite stand={stand} />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Allgemeine Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Zentralen({ zeilen }: { zeilen: SsZentrale[] }) {
  const spalten: Spalte<SsZentrale>[] = [
    { schluessel: 'kennzeichen', titel: 'Zentrale', wert: (z) => z.kennzeichen },
    { schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.baureihe },
    { schluessel: 'rolle', titel: 'Rolle', wert: (z) => (z.haengtAn ? `${z.rolle} → ${z.haengtAn}` : z.rolle) },
    { schluessel: 'pb', titel: 'P Betrieb', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.lastBetriebW },
    { schluessel: 'pa', titel: 'P Anlage', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.lastAnlageW },
    { schluessel: 'akku', titel: 'Akku', einheit: 'Ah', art: 'zahl', nachkomma: 0, wert: (z) => z.akkuAh },
    { schluessel: 'lader', titel: 'Ladeteil', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.ladestromA },
    { schluessel: 'pn', titel: 'Bemessungsleistung', art: 'zahl', nachkomma: 0,
      wert: (z) => z.bemessungsleistungZentrale ?? z.bemessungsleistungBaureihe },
    { schluessel: 'kreise', titel: 'SV-Kreise', art: 'zahl', wert: (z) => z.kreise },
    { schluessel: 'module', titel: 'Modulkreise', art: 'zahl', wert: (z) => z.modulkreiseVerfuegbar },
    {
      schluessel: 'befund', titel: 'Befund',
      wert: (z) => z.befunde.filter((b) => !b.startsWith('HINWEIS')).length,
      inhalt: (z) => {
        const n = z.befunde.filter((b) => !b.startsWith('HINWEIS')).length;
        return n === 0 ? '' : <StatusBadge zustand="warnung" text={`${n}`} klein />;
      },
    },
  ];
  return (
    <ContentSection titel="SV-Zentralen und Topologie"
                    beschreibung="Eine Unterstation ohne eigene Batterie hängt an der Batterie ihrer Hauptstation; ihre Last zählt dort mit. Bemessungsleistung: der Zentrale, sonst der Baureihe.">
      <DataTable name={`Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.kennzeichen} suche={false} dicht />
      {zeilen.filter((z) => z.befunde.length > 0).map((z) => (
        <div key={z.kennzeichen} className="karte">
          <h3>{z.kennzeichen}</h3>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Batterien({ zeilen }: { zeilen: SsBatterie[] }) {
  const spalten: Spalte<SsBatterie>[] = [
    { schluessel: 'station', titel: 'Station', wert: (z) => z.station },
    { schluessel: 'mit', titel: 'mit', wert: (z) => z.unterstationen.join(', ') },
    { schluessel: 'pb', titel: 'P_B', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.lastBetriebW },
    { schluessel: 'pa', titel: 'P_A', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.lastAnlageW },
    { schluessel: 'cmin', titel: 'C_min', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.mindestbedarfAh },
    { schluessel: 'cerf', titel: 'C_erf (C10)', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.kapazitaetErforderlichAh },
    { schluessel: 'cb', titel: 'C verbaut', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.bestandAh },
    { schluessel: 'ilmin', titel: 'I_L,min', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.ladestromMindestA },
    { schluessel: 'ilerf', titel: 'I_L,erf', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.ladestromErforderlichA },
    { schluessel: 'il', titel: 'I_L verbaut', einheit: 'A', art: 'zahl', nachkomma: 2, wert: (z) => z.ladestromA },
    { schluessel: 'q', titel: 'Lüftung Q', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (z) => z.lueftungM3h },
    { schluessel: 'a', titel: 'Querschnitt', einheit: 'cm²', art: 'zahl', nachkomma: 0, wert: (z) => z.lueftungQuerschnittCm2 },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="Batterie, Ladung und Lüftung"
                    beschreibung="C_erf = P_B · (1 + r) · t · f_A · f_T / (U_N · η · k); Mindestbedarf ohne Zuschlag C_min = P_B · t / U_N. Wiederaufladung 80 % in 12 h (DIN EN 50171), Lüftung Q = 0,05 · n · I_gas · C_N · 10⁻³ (DIN EN IEC 62485-2). * = gegen unbestätigte Vorgaben.">
      <DataTable name={`Hauptstationen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.station} suche={false} dicht />
      {zeilen.map((z) => (
        <div key={z.station} className="karte">
          <h3>{z.station}</h3>
          <p className="klein">{z.rechenweg.join(' · ')}</p>
          {z.bestandHerleitung && <p className="klein">verbaut: {z.bestandHerleitung}</p>}
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Fachplanung({ zeilen }: { zeilen: SsQuellanlage[] }) {
  const spalten: Spalte<SsQuellanlage>[] = [
    { schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.anlage },
    { schluessel: 'pb', titel: 'P Beleuchtung', einheit: 'W', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungBeleuchtungW },
    { schluessel: 'pa', titel: 'P Anlage Blatt', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.leistungAnlageW },
    { schluessel: 'c10', titel: 'C10 Blatt', einheit: 'Ah', art: 'zahl', nachkomma: 0, wert: (z) => z.kapazitaetAh },
    { schluessel: 'cerf', titel: 'C_erf nachger.', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.nachrechnung?.kapazitaetErforderlichAh ?? null },
    { schluessel: 'il', titel: 'I_L Blatt', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.ladestromA },
    { schluessel: 'q', titel: 'Q Blatt', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (z) => z.lueftungM3h },
    { schluessel: 'qn', titel: 'Q nachger.', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (z) => z.nachrechnung?.lueftungM3h ?? null },
    { schluessel: 'status', titel: 'Nachrechnung', art: 'status', wert: (z) => z.nachrechnung?.status ?? '',
      inhalt: (z) => (z.nachrechnung ? <Statusmarke status={z.nachrechnung.status} gegenAnnahmen={z.nachrechnung.gegenAnnahmen} /> : '—') },
  ];
  return (
    <ContentSection titel="Vergleich mit der Dimensionierung der Fachplanung"
                    beschreibung="Die Zahlen der Fachplanung stehen neben der Nachrechnung mit denselben Formeln. Geprüft wird das Modell; eine abweichende Anlage ist ein Befund (Fachfrage ELT-73). Eingelesen mit „sicherheitsstrom-einlesen“.">
      {zeilen.length === 0
        ? <p className="klein">Für dieses Projekt liegt keine Dimensionierung der Fachplanung vor.</p>
        : (
          <>
            <DataTable name={`Anlagen der Fachplanung (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                       schluessel={(z) => z.anlage} suche={false} dicht />
            {zeilen.map((z) => (
              <div key={z.anlage} className="karte">
                <h3>{z.anlage}</h3>
                {z.quelle && <p className="klein">Quelle: {z.quelle}</p>}
                <Befundliste befunde={z.befunde} />
              </div>
            ))}
          </>
        )}
    </ContentSection>
  );
}

function Kreise({ zeilen, spannungEndeV }: { zeilen: SsKreis[]; spannungEndeV: number }) {
  const [nurBefund, setNurBefund] = useState(true);
  const gezeigt = nurBefund ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<SsKreis>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale },
    { schluessel: 'verteiler', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'kreis', titel: 'Kreis', wert: (z) => z.kreis },
    { schluessel: 'kabeltyp', titel: 'Kabeltyp (EK)', wert: (z) => z.kabeltypBemessung ?? null },
    { schluessel: 'fe', titel: 'FE (EK)', wert: (z) => z.funktionserhaltBemessung ?? (z.kabeltypBemessung ? 'ohne' : null) },
    { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.laengeM },
    { schluessel: 'a', titel: 'A', einheit: 'mm²', art: 'zahl', nachkomma: 1, wert: (z) => z.querschnittMm2 },
    { schluessel: 'leuchten', titel: 'Leuchten', art: 'zahl', wert: (z) => z.leuchten },
    { schluessel: 'ib', titel: 'I Batterie', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.stromBatterieA },
    { schluessel: 'du', titel: 'ΔU', einheit: 'V', art: 'zahl', nachkomma: 2, wert: (z) => z.spannungsfallV },
    { schluessel: 'ul', titel: 'U Leuchte', einheit: 'V', art: 'zahl', nachkomma: 1, wert: (z) => z.spannungLeuchteV },
    { schluessel: 'in', titel: 'I Netz', einheit: 'A', art: 'zahl', nachkomma: 3, wert: (z) => z.stromNetzA },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} gegenAnnahmen={z.gegenAnnahmen} /> },
  ];
  return (
    <ContentSection titel="SV-Endstromkreise im Batteriebetrieb"
                    beschreibung={`Am Ende der Betriebsdauer steht die Batterie auf U_Ende = ${spannungEndeV.toLocaleString('de-DE')} V; die letzte Leuchte braucht ihre Mindestspannung. ΔU = 2 · ρ · L · I / A ab SV-Verteiler (Zuleitung fehlt, wo nicht gepflegt). Am Netz: Summenstrom gegen die Grenze des Stromkreismoduls. Kabeltyp und Funktionserhalt kommen aus der Kabelbemessung (EK) und werden hier nur angezeigt.`}>
      <label className="klein">
        <input type="checkbox" checked={nurBefund} onChange={(e) => setNurBefund(e.target.checked)} />
        {' '}nur Kreise mit Befund ({zeilen.filter((z) => z.status !== 'ok').length} von {zeilen.length})
      </label>
      <DataTable name={`Kreise (${gezeigt.length})`} spalten={spalten} zeilen={gezeigt}
                 schluessel={(z) => `${z.verteiler}|${z.kreis}`} dicht />
    </ContentSection>
  );
}

function Erkennungsweite({ stand }: { stand: SicherheitsstromStand }) {
  const w = stand.erkennungsweite;
  const spalten: Spalte<SsZeichen>[] = [
    { schluessel: 'schluessel', titel: 'Zeichen', wert: (z) => z.schluessel },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'abstand', titel: 'Abstand s', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.abstandM },
    { schluessel: 'herf', titel: 'h erforderlich', einheit: 'm', art: 'zahl', nachkomma: 3, wert: (z) => z.piktogrammhoeheErforderlichM },
    { schluessel: 'ueber', titel: 'über d', art: 'status', wert: (z) => z.ueberschritten,
      inhalt: (z) => <StatusBadge zustand={z.ueberschritten ? 'warnung' : 'gut'} text={z.ueberschritten ? 'ja' : 'nein'} klein /> },
  ];
  return (
    <ContentSection titel="Rettungszeichen: Erkennungsweite (Ersatzprüfung)"
                    beschreibung={ERKENNUNGSWEITE_HINWEIS}>
      <KpiReihe>
        <KpiCard titel="d = z · h" wert={`${w.erkennungsweiteM.toLocaleString('de-DE')} m`}
                 vergleich={`z ${w.distanzfaktor} · h ${w.piktogrammhoeheM.toLocaleString('de-DE')} m (Annahme)`} />
        <KpiCard titel="Abstand Median" wert={w.abstandMedianM === null ? '—' : `${w.abstandMedianM.toLocaleString('de-DE')} m`}
                 vergleich={w.abstandP95M === null ? undefined : `95 %: ${w.abstandP95M.toLocaleString('de-DE')} m`} />
        <KpiCard titel="größter Abstand" wert={w.abstandMaxM === null ? '—' : `${w.abstandMaxM.toLocaleString('de-DE')} m`} />
        <KpiCard titel="über d" wert={w.ueberschritten} zustand={w.ueberschritten > 0 ? 'warnung' : 'gut'}
                 zustandText={w.ueberschritten > 0 ? 'prüfen' : 'keine'} />
      </KpiReihe>
      <Befundliste befunde={w.befunde} />
      <DataTable name={`größte Abstände (${w.zeilen.length} von ${w.zeichen}; alle in Excel)`} spalten={spalten}
                 zeilen={w.zeilen} schluessel={(z) => z.schluessel} suche={false} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: SsVorgabe[] }) {
  const spalten: Spalte<SsVorgabe>[] = [
    { schluessel: 'feld', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts', wert: (z) => z.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => (z.rueckfall ? `${z.herkunft} (Rückfall)` : z.herkunft) },
    { schluessel: 'bestaetigt', titel: 'bestätigt', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein /> },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben"
                    beschreibung="Gepflegt unter Elektro-Vorgaben (elektro_vorgaben). „Rückfall“ heißt: keine Zeile im Projekt, gerechnet mit dem Wert der Migration 0930.">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}|${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
